import { BrowserProvider, JsonRpcSigner, getAddress } from "ethers";
import type { AppConfig, Eip1193Provider, WalletSession } from "./types";

const LAST_CONNECTOR_KEY = "l1x-safe:lastConnector";

interface WalletConnectProvider extends Eip1193Provider {
  readonly accounts?: string[];
  readonly session?: unknown;
  connect(): Promise<void>;
}

function injectedProvider(): Eip1193Provider | undefined {
  if (!window.ethereum) return undefined;
  const choices = window.ethereum.providers;
  if (choices?.length) {
    return choices.find((provider) => (provider as { isMetaMask?: boolean }).isMetaMask) ?? choices[0];
  }
  return window.ethereum;
}

function firstAuthorizedAccount(accounts: unknown): string | null {
  if (!Array.isArray(accounts) || typeof accounts[0] !== "string") return null;
  try {
    return getAddress(accounts[0]);
  } catch {
    return null;
  }
}

async function makeAuthorizedSession(
  eip1193: Eip1193Provider,
  kind: WalletSession["kind"],
  account: string,
): Promise<WalletSession> {
  const provider = new BrowserProvider(eip1193);
  const authorizedAccount = getAddress(account);
  // BrowserProvider.getSigner() can fall back to eth_requestAccounts. Automatic restores
  // and event refreshes must never prompt, so construct the signer from the account the
  // wallet has already returned instead.
  const signer = new JsonRpcSigner(provider, authorizedAccount);
  const network = await provider.getNetwork();
  return {
    kind,
    eip1193,
    provider,
    signer,
    account: authorizedAccount,
    chainId: Number(network.chainId),
  };
}

function walletConnectOptions(config: AppConfig, showQrModal: boolean) {
  return {
    projectId: config.walletConnectProjectId,
    chains: [config.chainId] as [number],
    optionalChains: [config.chainId],
    optionalMethods: [
      "eth_signTypedData_v4",
      "wallet_switchEthereumChain",
      "wallet_addEthereumChain",
    ],
    showQrModal,
    rpcMap: { [config.chainId]: config.rpcUrl },
    metadata: {
      name: "L1X Safe",
      description: "Multisig coordination on L1X",
      url: window.location.origin,
      icons: [`${window.location.origin}/favicon.svg`],
    },
  };
}

async function initWalletConnect(
  config: AppConfig,
  showQrModal: boolean,
): Promise<WalletConnectProvider> {
  const { EthereumProvider } = await import("@walletconnect/ethereum-provider");
  return EthereumProvider.init(
    walletConnectOptions(config, showQrModal),
  ) as unknown as Promise<WalletConnectProvider>;
}

async function walletConnectAccount(provider: WalletConnectProvider): Promise<string | null> {
  const loadedAccount = firstAuthorizedAccount(provider.accounts);
  if (loadedAccount) return loadedAccount;
  return firstAuthorizedAccount(await provider.request({ method: "eth_accounts" }));
}

export function lastConnector(): WalletSession["kind"] | null {
  try {
    const value = localStorage.getItem(LAST_CONNECTOR_KEY);
    if (value === "injected" || value === "walletconnect") return value;
    if (value !== null) localStorage.removeItem(LAST_CONNECTOR_KEY);
  } catch {
    // Storage can be disabled by browser privacy settings. Connection still works in memory.
  }
  return null;
}

export function rememberConnector(kind: WalletSession["kind"]): void {
  try {
    localStorage.setItem(LAST_CONNECTOR_KEY, kind);
  } catch {
    // Storage can be disabled by browser privacy settings. Keep the live session usable.
  }
}

export function clearLastConnector(): void {
  try {
    localStorage.removeItem(LAST_CONNECTOR_KEY);
  } catch {
    // A blocked storage API should never make disconnect fail.
  }
}

export function hasInjectedWallet(): boolean {
  return Boolean(injectedProvider());
}

export async function connectInjected(): Promise<WalletSession> {
  const eip1193 = injectedProvider();
  if (!eip1193) throw new Error("No injected wallet found. Install MetaMask or use WalletConnect.");
  const account = firstAuthorizedAccount(await eip1193.request({ method: "eth_requestAccounts" }));
  if (!account) throw new Error("The wallet did not authorize an account.");
  return makeAuthorizedSession(eip1193, "injected", account);
}

export async function connectWalletConnect(config: AppConfig): Promise<WalletSession> {
  if (!config.walletConnectProjectId) {
    throw new Error("WalletConnect is not configured for this build.");
  }
  const walletConnect = await initWalletConnect(config, true);
  if (!walletConnect.session) await walletConnect.connect();
  const account = await walletConnectAccount(walletConnect);
  if (!account) throw new Error("WalletConnect did not authorize an account.");
  return makeAuthorizedSession(walletConnect, "walletconnect", account);
}

export async function restoreInjected(): Promise<WalletSession | null> {
  const eip1193 = injectedProvider();
  if (!eip1193) {
    clearLastConnector();
    return null;
  }
  const account = firstAuthorizedAccount(await eip1193.request({ method: "eth_accounts" }));
  if (!account) {
    clearLastConnector();
    return null;
  }
  return makeAuthorizedSession(eip1193, "injected", account);
}

export async function restoreWalletConnect(config: AppConfig): Promise<WalletSession | null> {
  if (!config.walletConnectProjectId) {
    clearLastConnector();
    return null;
  }
  // init() loads WalletConnect's persisted session. With the modal disabled and no call to
  // connect(), this path can only restore an existing session and can never display a QR code.
  const walletConnect = await initWalletConnect(config, false);
  if (!walletConnect.session) {
    clearLastConnector();
    return null;
  }
  const account = await walletConnectAccount(walletConnect);
  if (!account) {
    clearLastConnector();
    return null;
  }
  return makeAuthorizedSession(walletConnect, "walletconnect", account);
}

export async function restoreLastWallet(config: AppConfig): Promise<WalletSession | null> {
  const connector = lastConnector();
  if (!connector) return null;
  try {
    return connector === "injected" ? await restoreInjected() : await restoreWalletConnect(config);
  } catch {
    // Silent restoration is best effort. A wallet/storage/relay failure must not block first
    // paint, surface an unhandled rejection, or trigger an interactive connection request.
    clearLastConnector();
    return null;
  }
}

export async function switchOrAddL1X(eip1193: Eip1193Provider, config: AppConfig) {
  const USER_REJECTED = 4001;
  const errCode = (e: unknown) => (e as { code?: number })?.code;
  const switchChain = () =>
    eip1193.request({
      method: "wallet_switchEthereumChain",
      params: [{ chainId: config.chainHex }],
    });
  try {
    await switchChain();
    return;
  } catch (error) {
    // The user said no: stop, do not escalate to an add prompt.
    if (errCode(error) === USER_REJECTED) throw error;
    // Anything else: the wallet most likely does not know chain 1066. Wallets differ in
    // how they signal that (MetaMask 4902, Rabby and others use different codes and plain
    // object errors), so attempt the add unconditionally.
  }
  await eip1193.request({
    method: "wallet_addEthereumChain",
    params: [
      {
        chainId: config.chainHex,
        chainName: config.chainName,
        nativeCurrency: { name: "L1X", symbol: config.nativeSymbol, decimals: 18 },
        rpcUrls: [config.rpcUrl, config.fallbackRpcUrl].filter(Boolean),
        blockExplorerUrls: [config.explorerUrl].filter(Boolean),
      },
    ],
  });
  // Some wallets add the chain but do not switch to it. Make the state deterministic.
  try {
    await switchChain();
  } catch (error) {
    if (errCode(error) === USER_REJECTED) throw error;
    // If the wallet auto-switched already, a redundant switch may error harmlessly.
  }
}

export async function refreshSession(
  session: WalletSession,
  knownAccount?: string,
): Promise<WalletSession | null> {
  const account = knownAccount
    ? firstAuthorizedAccount([knownAccount])
    : firstAuthorizedAccount(await session.eip1193.request({ method: "eth_accounts" }));
  if (!account) return null;
  return makeAuthorizedSession(session.eip1193, session.kind, account);
}
